/**
 * Fashion Hunter Collection - AJAX Filtering
 * Includes price range slider and Shopify Section Rendering API
 * Version: 2.1.0 - Fixed demo mode and event handling
 */

(function() {
  'use strict';

  const SELECTORS = {
    section: '.fashion-hunter-collection',
    filterBar: '[data-filter-bar]',
    filterTrigger: '[data-filter-trigger]',
    drawer: '[data-filter-drawer]',
    drawerOverlay: '[data-drawer-overlay]',
    drawerClose: '[data-drawer-close]',
    drawerApply: '[data-drawer-apply]',
    drawerStatus: '[data-drawer-status]',
    applyText: '[data-apply-text]',
    filterForm: '[data-filter-form]',
    filterGroup: '[data-filter-group]',
    groupToggle: '[data-group-toggle]',
    groupBody: '[data-group-body]',
    filterInput: '[data-filter-input]',
    sortInput: '[data-sort-input]',
    priceRange: '[data-price-range]',
    rangeMin: '[data-range-min]',
    rangeMax: '[data-range-max]',
    rangeProgress: '[data-range-progress]',
    displayMin: '[data-display-min]',
    displayMax: '[data-display-max]',
    activeFilters: '[data-active-filters]',
    removeFilter: '[data-remove-filter]',
    removePrice: '[data-remove-price]',
    clearAll: '[data-clear-all]',
    productsContainer: '[data-products-container]',
    paginationLink: '[data-pagination-link]',
    productCount: '[data-product-count]',
    loading: '[data-loading]'
  };

  let state = {
    isOpen: false,
    isLoading: false,
    sectionId: null,
    collectionUrl: null,
    abortController: null,
    isDemo: false
  };

  let elements = {};
  let priceSliders = [];

  // ==========================================
  // Price Range Slider
  // ==========================================
  
  class PriceRangeSlider {
    constructor(container) {
      this.container = container;
      this.min = parseFloat(container.dataset.min) || 0;
      this.max = parseFloat(container.dataset.max) || 1000;
      this.currentMin = parseFloat(container.dataset.currentMin) || this.min;
      this.currentMax = parseFloat(container.dataset.currentMax) || this.max;
      this.paramMin = container.dataset.paramMin;
      this.paramMax = container.dataset.paramMax;
      this.currency = container.dataset.currency || '$';
      
      this.rangeMin = container.querySelector(SELECTORS.rangeMin);
      this.rangeMax = container.querySelector(SELECTORS.rangeMax);
      this.progress = container.querySelector(SELECTORS.rangeProgress);
      this.displayMin = container.querySelector(SELECTORS.displayMin);
      this.displayMax = container.querySelector(SELECTORS.displayMax);
      
      this.init();
    }
    
    init() {
      if (!this.rangeMin || !this.rangeMax) return;
      
      this.rangeMin.addEventListener('input', () => this.handleInput('min'));
      this.rangeMax.addEventListener('input', () => this.handleInput('max'));
      this.rangeMin.addEventListener('change', () => this.handleChange());
      this.rangeMax.addEventListener('change', () => this.handleChange());
      
      this.update();
    }
    
    handleInput(type) {
      let minVal = parseFloat(this.rangeMin.value);
      let maxVal = parseFloat(this.rangeMax.value);
      const gap = (this.max - this.min) * 0.05;
      
      if (maxVal - minVal < gap) {
        if (type === 'min') {
          this.rangeMin.value = maxVal - gap;
          minVal = maxVal - gap;
        } else {
          this.rangeMax.value = minVal + gap;
          maxVal = minVal + gap;
        }
      }
      
      this.currentMin = minVal;
      this.currentMax = maxVal;
      this.update();
    }
    
    handleChange() {
      if (state.isDemo) return;
      
      clearTimeout(this.changeTimeout);
      this.changeTimeout = setTimeout(() => {
        applyFilters();
      }, 500);
    }
    
    update() {
      if (this.displayMin) {
        this.displayMin.textContent = `${this.currency}${Math.round(this.currentMin)}`;
      }
      if (this.displayMax) {
        this.displayMax.textContent = `${this.currency}${Math.round(this.currentMax)}`;
      }
      
      if (this.progress) {
        const percentMin = ((this.currentMin - this.min) / (this.max - this.min)) * 100;
        const percentMax = ((this.currentMax - this.min) / (this.max - this.min)) * 100;
        this.progress.style.left = `${percentMin}%`;
        this.progress.style.width = `${percentMax - percentMin}%`;
      }
    }
    
    getValues() {
      return {
        min: Math.round(this.currentMin),
        max: Math.round(this.currentMax),
        paramMin: this.paramMin,
        paramMax: this.paramMax,
        isDefault: this.currentMin === this.min && this.currentMax === this.max
      };
    }
    
    reset() {
      this.currentMin = this.min;
      this.currentMax = this.max;
      this.rangeMin.value = this.min;
      this.rangeMax.value = this.max;
      this.update();
    }
  }

  // ==========================================
  // Drawer Functions
  // ==========================================
  
  function openDrawer(targetGroup = null) {
    if (state.isOpen || !elements.drawer) return;
    
    state.isOpen = true;
    elements.drawer.classList.add('is-open');
    elements.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fh-drawer-open');
    
    if (targetGroup) {
      expandGroup(targetGroup);
    }
    
    setTimeout(() => {
      const closeBtn = elements.drawer.querySelector(SELECTORS.drawerClose);
      if (closeBtn) closeBtn.focus();
    }, 250);
  }
  
  function closeDrawer() {
    if (!state.isOpen || !elements.drawer) return;
    
    state.isOpen = false;
    elements.drawer.classList.remove('is-open');
    elements.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fh-drawer-open');
  }
  
  function expandGroup(groupName) {
    const group = document.querySelector(`[data-filter-group="${groupName}"]`);
    if (!group) return;
    
    const toggle = group.querySelector(SELECTORS.groupToggle);
    const body = group.querySelector(SELECTORS.groupBody);
    
    if (toggle && body) {
      toggle.setAttribute('aria-expanded', 'true');
      body.hidden = false;
      
      setTimeout(() => {
        group.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }
  
  function toggleGroup(toggle) {
    const body = toggle.parentElement.querySelector(SELECTORS.groupBody);
    if (!body) return;
    
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', !isExpanded);
    body.hidden = isExpanded;
  }

  // ==========================================
  // Filter Functions
  // ==========================================
  
  function buildFilterURL() {
    const form = elements.form;
    if (!form) return state.collectionUrl;
    
    const params = new URLSearchParams();
    
    const sortInput = form.querySelector(`${SELECTORS.sortInput}:checked`);
    if (sortInput) {
      params.set('sort_by', sortInput.value);
    }
    
    const inputs = form.querySelectorAll(SELECTORS.filterInput);
    inputs.forEach(input => {
      if (input.type === 'checkbox' && input.checked) {
        params.append(input.name, input.value);
      }
    });
    
    priceSliders.forEach(slider => {
      const values = slider.getValues();
      if (!values.isDefault) {
        params.set(values.paramMin, values.min * 100);
        params.set(values.paramMax, values.max * 100);
      }
    });
    
    const url = new URL(state.collectionUrl, window.location.origin);
    url.search = params.toString();
    return url.toString();
  }
  
  function clearAllFilters() {
    if (state.isDemo) {
      if (elements.form) elements.form.reset();
      priceSliders.forEach(slider => slider.reset());
      closeDrawer();
      return;
    }
    window.location.href = state.collectionUrl;
  }
  
  async function applyFilters(url = null) {
    if (state.isDemo) {
      console.log('Demo mode: Filters applied (no AJAX). URL:', buildFilterURL());
      closeDrawer();
      return;
    }
    
    if (state.isLoading && state.abortController) {
      state.abortController.abort();
    }
    
    state.isLoading = true;
    state.abortController = new AbortController();
    
    showLoading();
    
    const filterURL = url || buildFilterURL();
    
    try {
      const sectionURL = `${filterURL}${filterURL.includes('?') ? '&' : '?'}section_id=${state.sectionId}`;
      
      const response = await fetch(sectionURL, {
        signal: state.abortController.signal
      });
      
      if (!response.ok) throw new Error('Network error');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newProducts = doc.querySelector(SELECTORS.productsContainer);
      if (newProducts && elements.productsContainer) {
        elements.productsContainer.innerHTML = newProducts.innerHTML;
      }
      
      const newActiveFilters = doc.querySelector(SELECTORS.activeFilters);
      if (newActiveFilters && elements.activeFilters) {
        elements.activeFilters.outerHTML = newActiveFilters.outerHTML;
        elements.activeFilters = document.querySelector(SELECTORS.activeFilters);
      }
      
      const newStatus = doc.querySelector(SELECTORS.drawerStatus);
      const currentStatus = document.querySelector(SELECTORS.drawerStatus);
      if (newStatus && currentStatus) {
        currentStatus.textContent = newStatus.textContent;
      }
      
      const newCount = doc.querySelector(SELECTORS.productCount);
      const applyText = document.querySelector(SELECTORS.applyText);
      if (newCount && applyText) {
        applyText.textContent = `VIEW ${newCount.textContent.trim()}+ ITEMS`;
      }
      
      window.history.pushState({}, '', filterURL);
      reinitializeEvents();
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Filter error:', error);
      }
    } finally {
      state.isLoading = false;
      hideLoading();
    }
  }
  
  function showLoading() {
    if (elements.loading) {
      elements.loading.hidden = false;
      elements.loading.style.display = 'flex';
    }
    if (elements.productsContainer) {
      elements.productsContainer.classList.add('is-loading');
    }
  }
  
  function hideLoading() {
    if (elements.loading) {
      elements.loading.hidden = true;
      elements.loading.style.display = 'none';
    }
    if (elements.productsContainer) {
      elements.productsContainer.classList.remove('is-loading');
    }
  }

  // ==========================================
  // Event Handlers
  // ==========================================
  
  function handleDocumentClick(e) {
    // Filter trigger
    const trigger = e.target.closest(SELECTORS.filterTrigger);
    if (trigger) {
      e.preventDefault();
      openDrawer(trigger.dataset.filterTrigger);
      return;
    }
    
    // Drawer close (overlay or close button)
    if (e.target.matches(SELECTORS.drawerOverlay) || e.target.closest(SELECTORS.drawerClose)) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    
    // Group toggle
    const groupToggle = e.target.closest(SELECTORS.groupToggle);
    if (groupToggle) {
      e.preventDefault();
      toggleGroup(groupToggle);
      return;
    }
    
    // Apply button
    const applyBtn = e.target.closest(SELECTORS.drawerApply);
    if (applyBtn) {
      e.preventDefault();
      if (state.isDemo) {
        closeDrawer();
      } else {
        applyFilters();
        closeDrawer();
      }
      return;
    }
    
    // Clear all
    const clearBtn = e.target.closest(SELECTORS.clearAll);
    if (clearBtn) {
      e.preventDefault();
      clearAllFilters();
      return;
    }
    
    // Remove filter pill
    const removeBtn = e.target.closest(SELECTORS.removeFilter);
    if (removeBtn) {
      e.preventDefault();
      const param = removeBtn.dataset.param;
      const value = removeBtn.dataset.value;
      const input = elements.form?.querySelector(`input[name="${param}"][value="${value}"]`);
      if (input) input.checked = false;
      if (!state.isDemo) applyFilters();
      return;
    }
    
    // Remove price filter
    const removePriceBtn = e.target.closest(SELECTORS.removePrice);
    if (removePriceBtn) {
      e.preventDefault();
      priceSliders.forEach(slider => slider.reset());
      if (!state.isDemo) applyFilters();
      return;
    }
    
    // Pagination
    const paginationLink = e.target.closest(SELECTORS.paginationLink);
    if (paginationLink) {
      e.preventDefault();
      if (!state.isDemo) applyFilters(paginationLink.href);
      return;
    }
  }
  
  function handleKeydown(e) {
    if (e.key === 'Escape' && state.isOpen) {
      closeDrawer();
    }
  }
  
  function handleFormChange(e) {
    if (state.isDemo) return;
    
    const input = e.target;
    if (!input.matches(SELECTORS.filterInput) && !input.matches(SELECTORS.sortInput)) return;
    
    clearTimeout(handleFormChange.timeout);
    handleFormChange.timeout = setTimeout(() => applyFilters(), 300);
  }

  function reinitializeEvents() {
    // Re-cache active filters element
    elements.activeFilters = document.querySelector(SELECTORS.activeFilters);
  }
  
  function detectDemoMode() {
    const isLocalFile = window.location.protocol === 'file:';
    const isDemo = document.querySelector('[data-section-id="demo"]') !== null;
    const noShopify = typeof Shopify === 'undefined';
    return isLocalFile || isDemo || noShopify;
  }
  
  function init() {
    const section = document.querySelector(SELECTORS.section);
    if (!section) return;
    
    state.isDemo = detectDemoMode();
    state.sectionId = section.dataset.sectionId;
    state.collectionUrl = section.dataset.collectionUrl || window.location.pathname;
    
    elements = {
      section,
      drawer: document.querySelector(SELECTORS.drawer),
      form: document.querySelector(SELECTORS.filterForm),
      productsContainer: document.querySelector(SELECTORS.productsContainer),
      activeFilters: document.querySelector(SELECTORS.activeFilters),
      loading: document.querySelector(SELECTORS.loading)
    };
    
    // Ensure loading is hidden on init
    hideLoading();
    
    // Initialize price sliders
    document.querySelectorAll(SELECTORS.priceRange).forEach(container => {
      priceSliders.push(new PriceRangeSlider(container));
    });
    
    // Single document click handler
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleKeydown);
    
    if (elements.form) {
      elements.form.addEventListener('change', handleFormChange);
    }
    
    window.addEventListener('popstate', () => {
      if (!state.isDemo) applyFilters(window.location.href);
    });
    
    if (state.isDemo) {
      console.log('Fashion Hunter Collection: Demo mode active');
    }
  }
  
  // Public API
  window.FashionHunterCollection = {
    open: openDrawer,
    close: closeDrawer,
    apply: applyFilters,
    clear: clearAllFilters
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(function () {
  var gesture = null;

  function getCard(element) {
    return element ? element.closest('[data-product-card]') : null;
  }

  function setSecondary(card, showSecondary) {
    if (!card || !card.querySelector('[data-secondary-image]')) return;
    card.classList.toggle('is-secondary-visible', showSecondary);
    var toggle = card.querySelector('[data-secondary-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-pressed', showSecondary ? 'true' : 'false');
    }
  }

  function resetSecondary(card) {
    setSecondary(card, false);
  }

  function updateVariantImage(swatch) {
    var card = getCard(swatch);
    if (!card) return;

    var primary = card.querySelector('[data-primary-image]');
    var imageSrc = swatch.getAttribute('data-image-src');
    var imageSrcset = swatch.getAttribute('data-image-srcset');
    var variantUrl = swatch.getAttribute('data-variant-url');

    if (primary && imageSrc) {
      primary.src = imageSrc;
      if (imageSrcset) primary.srcset = imageSrcset;
    }

    if (variantUrl) {
      card.querySelectorAll('[data-card-link], [data-card-title-link]').forEach(function (link) {
        link.href = variantUrl;
      });
    }

    card.querySelectorAll('[data-fh-card-swatch]').forEach(function (button) {
      var active = button === swatch;
      button.classList.toggle('fashion-hunter-product-card__swatch--active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    resetSecondary(card);
  }

  document.addEventListener('click', function (event) {
    var swatch = event.target.closest('[data-fh-card-swatch]');
    if (swatch) {
      event.preventDefault();
      event.stopPropagation();
      updateVariantImage(swatch);
      return;
    }

    var toggle = event.target.closest('[data-secondary-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      var toggleCard = getCard(toggle);
      if (toggleCard) {
        setSecondary(toggleCard, !toggleCard.classList.contains('is-secondary-visible'));
      }
      return;
    }

    var link = event.target.closest('[data-card-link]');
    if (link) {
      var linkCard = getCard(link);
      if (linkCard) {
        var suppressUntil = Number(linkCard.dataset.fhSuppressNavUntil || 0);
        if (Date.now() < suppressUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
  }, true);

  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType !== 'touch') return;
    var media = event.target.closest('[data-card-media]');
    if (!media) return;
    var card = getCard(media);
    if (!card || !card.querySelector('[data-secondary-image]')) return;

    gesture = {
      card: card,
      startX: event.clientX,
      startY: event.clientY
    };
  }, { passive: true });

  document.addEventListener('pointerup', function (event) {
    if (!gesture || event.pointerType !== 'touch') return;

    var deltaX = event.clientX - gesture.startX;
    var deltaY = event.clientY - gesture.startY;
    var card = gesture.card;
    gesture = null;

    if (Math.abs(deltaX) < 36 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    setSecondary(card, deltaX < 0);
    card.dataset.fhSuppressNavUntil = String(Date.now() + 600);
  }, { passive: true });

  document.addEventListener('pointercancel', function () {
    gesture = null;
  }, { passive: true });
})();
